import { PropertyPanelDevice } from '@shure/cloud/shared/models/devices';
import { mapDurationFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';

import { DeviceStatus, PropertyPanelDeviceFragment } from '../graphql/generated/cloud-sys-api';

import {
	AllAuracastAudioChannelFeatureFragments,
	AllAuracastDeviceFeatureFragments,
	mapAuracastFromSysApi
} from './map-auracast';
import { mapBatteryStateFromSysApi } from './map-battery-state';
import { mapCloudRoomFromSysApi } from './map-cloud-room';
import { mapDeviceAuthenticationFromSysApi } from './map-device-authentication';
import { mapExtendedWarrantyFromSysApi } from './map-extended-warranty';
import { mapFirmwarePackagesFromSysApi } from './map-firmware-packages';
import { mapSysApiFirmwareUpdateRequestToFeature } from './map-firmware-update-requests';
import { mapGroupFromSysApi } from './map-group';
import { mapInternetOfThingsFromSysApi } from './map-internet-of-things';
import { mapLicenseInfoFromSysApi } from './map-license-info';
import { mapLicenseInfoV2FromSysApi } from './map-license-info-v2';
import { mapLicenseV3FromSysApi } from './map-license-v3';
import { mapLicenseV4FromSysApi } from './map-license-v4';
import { mapPairedDevicesFromSysApi } from './map-paired-devices';
import { mapFirmwareUpdateJobRequestFromSysApi, mapRebootJobRequestFromSysApi } from './map-scheduled-job-request';
import {
	deviceFragmentToMicStateFeature,
	mapCompatibilityLevelFromSysApi,
	mapConferencingPlatformFromSysApi,
	mapDeviceModelFromSysApi,
	mapExternalDisplaysFromSysApi,
	mapFeatureValueFromSysApi,
	mapHealthFeatureFromSysApi,
	mapHostedFirmwareVersionFromSysApi,
	mapIpModeFromSysApi,
	mapLastBootTimeFromSysApi,
	mapRfChannelsFromSysApi
} from './utils';

export function mapPropertyPanelDeviceFromSysApi(
	deviceSysApi: PropertyPanelDeviceFragment,
	auracastChannel: AllAuracastAudioChannelFeatureFragments | null
): PropertyPanelDevice {
	const {
		id,
		hardwareId,
		virtual,
		compatibility,
		description: { features: descriptionFeatures, constraints: constraintFeatures },
		interface: { model: deviceModel },
		features: {
			audioChannelCount,
			audioMute,
			audioNetwork,
			auracast,
			authentication,
			availablePackages,
			batteryLevel,
			cloudRoom,
			conferencingPlatform,
			controlNetwork,
			danteAudioNetwork,
			extendedWarranty,
			externalDisplays,
			firmware,
			group,
			health,
			hostedFirmware,
			identify,
			internetOfThings,
			iotRoomProxy,
			license,
			licenseV2,
			licenseV3,
			licenseV4,
			name,
			pairedDevices,
			rfChannels,
			room,
			scheduledJobs,
			serialNumber,
			tags,
			lastBootTime
		}
	} = deviceSysApi;

	const firmwareJobRequests = scheduledJobs?.firmwareJobRequests;
	const rebootJobRequests = scheduledJobs?.rebootJobRequests;

	// Create canonical Firmware Update Request V2 elements from the V1 updateProgress and hostedUpdateProgress

	// the FW Update V2 feature
	const primaryFirmwareUpdateRequestFeature = mapSysApiFirmwareUpdateRequestToFeature(
		deviceSysApi.description.features.firmwareUpdateRequests?.supported,
		deviceSysApi.features.firmwareUpdateRequests?.primary?.current
	);
	const hostedFirmwareUpdateRequestFeature = mapSysApiFirmwareUpdateRequestToFeature(
		deviceSysApi.description.features.firmwareUpdateRequests?.supported,
		deviceSysApi.features.firmwareUpdateRequests?.hosted.current
	);

	const auracastDevice: AllAuracastDeviceFeatureFragments | null = descriptionFeatures.auracast?.supported
		? {
				auracast: deviceSysApi.features.auracast,
				bluetoothPower: deviceSysApi.features.bluetoothPower
			}
		: null;

	const device: PropertyPanelDevice = {
		id,
		hardwareId,
		name: mapFeatureValueFromSysApi(name?.name, ''),
		model: mapDeviceModelFromSysApi(deviceModel ?? ''),
		status: deviceSysApi.status,
		isVirtual: virtual,
		compatibility: mapCompatibilityLevelFromSysApi(compatibility),
		manufacturer: deviceSysApi.manufacturer,
		constraints: {
			name: {
				regex: constraintFeatures?.name?.name.pattern ?? ''
			}
		},

		features: {
			audioChannelCount: {
				count: audioChannelCount?.count ?? 0,
				isMissing: !descriptionFeatures.audioChannelCount?.supported
			},
			mute: {
				muted: audioMute?.muted || false,
				isMissing: !descriptionFeatures.audioMute.supported
			},
			audioNetwork: {
				gateway: mapFeatureValueFromSysApi(audioNetwork?.interface.gateway),
				ipAddress: mapFeatureValueFromSysApi(audioNetwork?.interface.ipAddress),
				ipMode: mapIpModeFromSysApi(audioNetwork?.interface.ipMode),
				subnetMask: mapFeatureValueFromSysApi(audioNetwork?.interface.subnetMask),
				macAddress: mapFeatureValueFromSysApi(audioNetwork?.interface.macAddress),
				isMissing: !descriptionFeatures.audioNetwork.supported
			},
			auracast: descriptionFeatures.auracast?.supported
				? {
						...mapAuracastFromSysApi(auracastDevice, auracastChannel),
						isMissing: !auracast
					}
				: undefined,
			authentication: {
				...mapDeviceAuthenticationFromSysApi(authentication),
				isMissing: !descriptionFeatures.authentication?.supported
			},
			availablePackages: {
				...mapFirmwarePackagesFromSysApi(availablePackages),
				isMissing: !descriptionFeatures.availablePackages.supported
			},
			batteryLevel: {
				percentage: batteryLevel?.percentage ?? 0,
				currentState: mapBatteryStateFromSysApi(batteryLevel?.status.currentState),
				targetState: mapBatteryStateFromSysApi(batteryLevel?.status.targetState),
				timeToTargetState: mapDurationFromSysApi(batteryLevel?.status.timeToTargetState),
				isMissing: !descriptionFeatures.batteryLevel?.supported
			},
			cloudRoom: descriptionFeatures.cloudRoom?.supported
				? {
						cloudRoom: mapCloudRoomFromSysApi(cloudRoom?.room),
						isMissing: !cloudRoom
					}
				: undefined,
			conferencingPlatform: descriptionFeatures.conferencingPlatform?.supported
				? {
						platform: mapConferencingPlatformFromSysApi(conferencingPlatform?.platform),
						isMissing: !conferencingPlatform
					}
				: undefined,
			controlNetwork: {
				ipMode: mapIpModeFromSysApi(controlNetwork?.interface.ipMode),
				ipAddress:
					deviceSysApi.status === DeviceStatus.Error
						? mapFeatureValueFromSysApi(deviceSysApi.protocol.dmp?.ipAddress)
						: mapFeatureValueFromSysApi(controlNetwork?.interface.ipAddress),
				subnetMask: mapFeatureValueFromSysApi(controlNetwork?.interface.subnetMask),
				gateway: mapFeatureValueFromSysApi(controlNetwork?.interface.gateway),
				macAddress: mapFeatureValueFromSysApi(controlNetwork?.interface.macAddress),
				isMissing: !descriptionFeatures.controlNetwork.supported
			},
			danteName: {
				danteName: mapFeatureValueFromSysApi(danteAudioNetwork?.name),
				isMissing: !descriptionFeatures.danteAudioNetwork.supported
			},
			extendedWarranty: descriptionFeatures.extendedWarranty?.supported
				? {
						...mapExtendedWarrantyFromSysApi(extendedWarranty),
						isMissing: !extendedWarranty
					}
				: undefined,
			externalDisplays: {
				...mapExternalDisplaysFromSysApi(externalDisplays?.displays ?? []),
				isMissing: !descriptionFeatures.externalDisplays?.supported
			},
			firmware: {
				version: mapFeatureValueFromSysApi(firmware?.version),
				valid: firmware?.valid ?? true,
				isMissing: !descriptionFeatures.firmware.supported
			},
			group: descriptionFeatures.group?.supported
				? {
						group: mapGroupFromSysApi(group?.group),
						isMissing: !group
					}
				: undefined,
			health: mapHealthFeatureFromSysApi(descriptionFeatures.health?.supported, health),
			hostedFirmware: {
				version: mapHostedFirmwareVersionFromSysApi(hostedFirmware?.version),
				key: mapFeatureValueFromSysApi(hostedFirmware?.key, ''),
				isMissing: !descriptionFeatures.hostedFirmware?.supported
			},
			primaryFirmwareUpdateRequest: primaryFirmwareUpdateRequestFeature,
			hostedFirmwareUpdateRequest: hostedFirmwareUpdateRequestFeature,
			internetOfThings: !descriptionFeatures.internetOfThings.supported
				? undefined
				: {
						...mapInternetOfThingsFromSysApi(internetOfThings),
						isMissing: !internetOfThings
					},
			identify: {
				identifying: identify?.identifying || false,
				isMissing: !descriptionFeatures.identify.supported
			},
			iotRoomProxy: descriptionFeatures.iotRoomProxy?.supported
				? {
						enabled: iotRoomProxy?.enabled ?? false,
						isMissing: !iotRoomProxy
					}
				: undefined,
			license: descriptionFeatures.license?.supported
				? {
						info: mapLicenseInfoFromSysApi(license?.info ?? []),
						isMissing: !license
					}
				: undefined,
			licenseV2: descriptionFeatures.licenseV2?.supported
				? {
						info: mapLicenseInfoV2FromSysApi(licenseV2?.info ?? []),
						isMissing: !licenseV2
					}
				: undefined,
			licenseV3: descriptionFeatures.licenseV3?.supported
				? {
						license: mapLicenseV3FromSysApi(licenseV3),
						isMissing: !licenseV3
					}
				: undefined,
			licenseV4: descriptionFeatures.licenseV4?.supported
				? {
						license: mapLicenseV4FromSysApi(licenseV4),
						isMissing: !licenseV4
					}
				: undefined,
			micStatus: deviceFragmentToMicStateFeature(deviceSysApi),
			name: {
				name: name?.name,
				isMissing: !descriptionFeatures.name?.supported
			},
			pairedDevices: descriptionFeatures.pairedDevices?.supported
				? {
						devices: pairedDevices?.pairedDevices
							? mapPairedDevicesFromSysApi(pairedDevices.pairedDevices)
							: [],
						isMissing: !pairedDevices
					}
				: undefined,
			// reboot only has a feature description. No feature value
			reboot: {
				supported: descriptionFeatures.reboot?.supported ?? false
			},
			rfChannels: descriptionFeatures.rfChannels?.supported
				? {
						channels: mapRfChannelsFromSysApi(rfChannels?.rfChannels ?? []),
						isMissing: !rfChannels
					}
				: undefined,
			room: descriptionFeatures.room?.supported
				? {
						room: {
							name: mapFeatureValueFromSysApi(deviceSysApi.features.room?.room?.name)
						},
						isMissing: !room
					}
				: undefined,
			scheduledJobs: descriptionFeatures.scheduledJobs?.supported
				? {
						firmwareUpdateJobRequests: {
							pendingPrimary: mapFirmwareUpdateJobRequestFromSysApi(
								firmwareJobRequests?.pending?.primary
							),
							pendingHosted: mapFirmwareUpdateJobRequestFromSysApi(firmwareJobRequests?.pending?.hosted)
						},
						rebootJobRequests: {
							pending: mapRebootJobRequestFromSysApi(rebootJobRequests?.pending)
						},
						isMissing: !scheduledJobs
					}
				: undefined,
			serialNumber: {
				serialNumber: mapFeatureValueFromSysApi(serialNumber?.serialNumber),
				isMissing: !descriptionFeatures.serialNumber.supported
			},
			lastBootTime: {
				lastBootTime: mapLastBootTimeFromSysApi(lastBootTime?.lastBootTime),
				isMissing: !descriptionFeatures.lastBootTime?.supported
			},
			tags: {
				tags: tags?.tags ?? [],
				isMissing: !descriptionFeatures.tags?.supported
			}
		}
	};

	return device;
}
